import { useEffect, useState } from "react";
import holorayLogo from "../../assets/images/png/HoloRay_Logo_256.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faCheck, faUsers, faShieldAlt, faLock } from "@fortawesome/free-solid-svg-icons";
import ChannelConnection from "../../js/connection/ChannelConnection";
import LeaveChannelModal from "./LeaveChannelModal";
import { useNavigate } from "react-router";

export default function ChannelBanner({ connection }: { connection: ChannelConnection }) {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [participantCount, setParticipantCount] = useState(1);

    useEffect(() => {
        const updateCount = () => {
            const count = Object.values(connection.sfuConnection.connections)
                .filter(c => !c.isScreen).length;
            setParticipantCount(Math.max(1, count));
        };
        connection.events.addListener("onconnectionopen", updateCount);
        connection.events.addListener("onconnectionclose", updateCount);
        updateCount();
        return () => {
            connection.events.removeListener("onconnectionopen", updateCount);
            connection.events.removeListener("onconnectionclose", updateCount);
        };
    }, [connection]);

    function copySessionId() {
        navigator.clipboard.writeText(connection.channel.getId()).catch(() => {
            const el = document.createElement('textarea');
            el.value = connection.channel.getId();
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
        });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    function onLeaveCancel() {
        const confirmLeave = document.getElementById("confirmLeave");
        if (confirmLeave) confirmLeave.classList.remove("is-active");
    }

    return (
        <>
            <div className="channel-banner">
                <div className="banner-left">
                    <img src={holorayLogo} alt="HoloRay" className="banner-logo" />
                    <span className="banner-wordmark">HoloXR <span className="banner-wordmark-light">Light</span></span>
                    <div className="banner-divider" />
                    <div className="live-pill">
                        <span className="live-dot" />
                        <span>Live</span>
                    </div>
                    <button className="session-badge" onClick={copySessionId} title="Copy session ID">
                        <span className="session-label">Session</span>
                        <span className="session-id">{connection.channel.getId()}</span>
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="copy-icon" />
                    </button>
                </div>

                <div className="banner-right">
                    <div className="banner-stat">
                        <FontAwesomeIcon icon={faUsers} className="stat-icon" />
                        <span className="stat-value">{participantCount}</span>
                    </div>
                    <div className="banner-badge hipaa">
                        <FontAwesomeIcon icon={faShieldAlt} className="badge-icon" />
                        <span>HIPAA Compliant</span>
                    </div>
                    <div className="banner-badge secure">
                        <FontAwesomeIcon icon={faLock} className="badge-icon" />
                        <span>Encrypted</span>
                    </div>
                </div>
            </div>

            <LeaveChannelModal
                title="Leave the session?"
                onSuccess={() => {
                    connection.leaveChannel();
                    navigate("/");
                }}
                onCancel={onLeaveCancel}
            />

            <style>{`
                .channel-banner {
                    background: var(--hx-card);
                    border-bottom: 1px solid var(--hx-line);
                    height: 56px;
                    padding: 0 1.25rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-shrink: 0;
                    position: relative;
                    z-index: 40;
                    -webkit-transform: translateZ(0);
                    transform: translateZ(0);
                }

                .banner-left {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    min-width: 0;
                }

                .banner-logo {
                    height: 26px;
                    width: auto;
                    flex-shrink: 0;
                }

                .banner-wordmark {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: var(--hx-ink);
                    letter-spacing: -0.01em;
                    flex-shrink: 0;
                    white-space: nowrap;
                }

                .banner-wordmark-light {
                    font-weight: 500;
                    color: var(--hx-muted);
                }

                .banner-divider {
                    width: 1px;
                    height: 20px;
                    background: var(--hx-line);
                    flex-shrink: 0;
                }

                .live-pill {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 0.22rem 0.6rem;
                    border-radius: 999px;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    color: #dc2626;
                    font-size: 0.68rem;
                    font-weight: 700;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    flex-shrink: 0;
                }

                .live-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #ef4444;
                    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.55);
                    animation: livePulse 1.6s ease-out infinite;
                }

                @keyframes livePulse {
                    0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
                    70% { box-shadow: 0 0 0 7px rgba(239, 68, 68, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
                }

                .session-badge {
                    display: flex;
                    align-items: center;
                    gap: 0.45rem;
                    background: var(--hx-line-2);
                    border: 1px solid var(--hx-line);
                    border-radius: 999px;
                    padding: 0.28rem 0.7rem;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    font-family: inherit;
                    min-width: 0;
                }

                .session-badge:hover {
                    background: var(--hx-blue-soft);
                    border-color: var(--hx-blue-border);
                }

                .session-label {
                    font-size: 0.65rem;
                    font-weight: 650;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: var(--hx-muted);
                }

                .session-id {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: var(--hx-ink-2);
                    font-family: 'SF Mono', 'Fira Code', ui-monospace, monospace;
                    letter-spacing: 0.02em;
                    max-width: 160px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .copy-icon {
                    color: var(--hx-muted);
                    font-size: 0.7rem;
                    flex-shrink: 0;
                }

                .session-badge:hover .copy-icon { color: var(--hx-blue); }

                .banner-right {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    flex-shrink: 0;
                }

                .banner-stat {
                    display: flex;
                    align-items: center;
                    gap: 0.35rem;
                    color: var(--hx-muted);
                }

                .stat-icon { font-size: 0.72rem; color: var(--hx-muted); }

                .stat-value {
                    font-size: 0.8rem;
                    color: var(--hx-ink-2);
                    font-weight: 500;
                }

                .banner-badge {
                    display: flex;
                    align-items: center;
                    gap: 0.35rem;
                    padding: 0.28rem 0.6rem;
                    border-radius: 999px;
                    font-size: 0.72rem;
                    font-weight: 600;
                    white-space: nowrap;
                }

                .banner-badge .badge-icon { font-size: 0.7rem; }

                .banner-badge.hipaa {
                    background: var(--hx-blue-soft);
                    border: 1px solid var(--hx-blue-border);
                    color: var(--hx-blue);
                }

                .banner-badge.secure {
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    color: var(--hx-success);
                }

                @media (max-width: 768px) {
                    .channel-banner {
                        padding: 0 0.75rem;
                        height: 48px;
                        padding-left: max(0.75rem, env(safe-area-inset-left, 0px));
                        padding-right: max(0.75rem, env(safe-area-inset-right, 0px));
                    }
                    .banner-logo { height: 20px; }
                    .banner-wordmark { display: none; }
                    .session-label { display: none; }
                    .session-id { max-width: 88px; font-size: 0.7rem; }
                    .banner-badge.hipaa,
                    .banner-badge.secure span { display: none; }
                    .banner-badge { padding: 0.3rem; }
                    .live-pill span:last-child { display: none; }
                    .live-pill { padding: 0.28rem; }
                    .banner-right { gap: 0.4rem; }
                }

                @media (max-width: 380px) {
                    .banner-divider { display: none; }
                    .session-id { max-width: 64px; }
                }
            `}</style>
        </>
    );
}
